import { axios } from '@boot/axios.js';
import { Notify } from 'quasar';
import { i18n } from '@boot/i18n.js';

// Get the local timezone
const localTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

/**
 * Formats a data removing the time
 * @param {String} date - A date
 * @returns {String} - Year, month and day separated by /
 */
function getFormattedDate(date) {
    return new Date(date).toLocaleDateString('en-GB', {
        timeZone: localTimezone
    });
}

/**
 * Schema representing a device's disconnection report to be used in the frontend
 * Used to map the response from the backend
 */
export class DeviceDisconnectionReport {
    constructor(obj = {}) {
        (this.id = obj['device_id'] || null),
            (this.isOffline = obj['offline'] || false),
            (this.totalDurationInSeconds = obj['total_duration_s'] || 0),
            (this.totalDisconnectionsCount =
                obj['total_nb_disconnections'] || 0),
            (this.uptime = 100),
            (this.timeline = new Map());

        if (obj['timeline']) {
            obj.timeline.forEach(item => {
                // Get date formatted to be used as the map key
                const date = getFormattedDate(item['date']);

                this.timeline.set(date, {
                    durationInSeconds: item['duration_s'],
                    count: item['nb_disconnections'],
                    hasDisconnection: item['nb_disconnections'] > 0,
                    date: date
                });
            });
        }
    }
}

/**
 * Schema representing a device to be used in the frontend
 * Used to map the response from the backend
 */
export class Device {
    constructor(obj = {}) {
        (this.id = obj['id']),
            (this.uuid = obj['uuid']),
            (this.facility = obj['facility']),
            (this.deviceType = obj['device_type']),
            (this.serialNumber = obj['serial_number']),
            (this.macAddress = obj['mac_address']),
            (this.ipAddress = obj['ip_address']),
            (this.mqttClientId = obj['mqtt_client_id']),
            (this.mqttUsername = obj['mqtt_username']),
            (this.assets = []),
            (this.notifiedUsers = obj['notified_users']);

        if (obj['assets']) {
            obj.assets.forEach(asset => {
                const formattedAsset = {
                    id: asset['id'],
                    uuid: asset['uuid'],
                    name: asset['name']
                };
                this.assets.push(formattedAsset);
            });
        }
    }
}

/**
 * Schema representing a disconnection to be used in the frontend
 * Used to map the response from the backend
 */
export class Disconnection {
    constructor(obj = {}) {
        (this.id = obj['id']),
            (this.start = obj['start']),
            (this.end = obj['end']),
            (this.duration_s = obj['duration_s']),
            (this.description = obj['description']);
    }
}

export default class DeviceManager {
    constructor(timezone, user) {
        this.timezone = timezone;
        this.user = user;
    }

    // API FETCH FUNCTIONS

    /**
     * Get all devices data for a given facility
     * @param {Number} facility - Facility Id
     * @return {Array} - Array of device objects
     */
    async _fetchAllDevices(facility) {
        try {
            const response = await axios.get(`devices/?facility=${facility}`);
            const data = this._processDevicesResponse(response.data);
            return data;
        } catch (error) {
            console.error('Failed to fetch devices due to: ', error);
            // Show error notification to user
            Notify.create({
                message: i18n.global.t(
                    'Failed to fetch devices data. Please try again later.'
                ),
                color: 'red',
                textColor: 'white',
                position: 'top'
            });
        }
    }

    /**
     * Get disconnections report data
     * @param {Object} params - Query parameters to filter the data
     * @return {Array} - Array of disconnection objects with pagination data
     */
    async _fetchAllDisconnections(params) {
        // build query params in string url
        const queryParams = Object.keys(params)
            .map(key => `${key}=${params[key]}`)
            .join('&');
        const url = `disconnections/?${queryParams}`;

        try {
            const response = await axios.get(url);
            const data = this._processDisconnectionsResponse(response.data);
            return data;
        } catch (error) {
            console.error('Failed to fetch disconnections due to: ', error);
            // Show error notification to user
            Notify.create({
                message: i18n.global.t(
                    'Failed to fetch disconnection data. Please try again later.'
                ),
                color: 'red',
                textColor: 'white',
                position: 'top'
            });
        }
    }

    /**
     * Process the data from the API to be used by the frontend.
     * @param {Object} response
     * @returns Formatted object
     */
    _processDevicesResponse(response) {
        const data = {
            count: response.count,
            next: response.next,
            previous: response.previous,
            results: []
        };

        response.results.forEach(device => {
            const processedDevice = new Device(device);
            // Process the device and add a new property
            // notifications to it.
            const processDeviceWithNotificationStatus = {
                ...processedDevice,
                notifications: {
                    originalNotificationStatus:
                        processedDevice.notifiedUsers.includes(this.user),
                    isNotified: processedDevice.notifiedUsers.includes(
                        this.user
                    )
                }
            };
            data.results.push(processDeviceWithNotificationStatus);
        });
        return data;
    }

    _processDisconnectionsResponse(response) {
        const data = {
            count: response.count,
            next: response.next,
            previous: response.previous,
            results: []
        };

        response.results.forEach(disconnection => {
            const processedDisconnection = new Disconnection(disconnection);
            data.results.push(processedDisconnection);
        });

        return data;
    }

    /**
     * Get disconnection report for a given facility
     * @param {Number} facility - Facility Id
     * @param {String} start - Starting date for the period
     * @param {String} end - Ending date for the period
     * @return {Object} - Array of objects with disconnection data, start and end dates
     */
    async _fetchDisconnectionsReport(facility, start, end) {
        try {
            const response = await axios.get(
                `disconnections/report/?facility_id=${facility}&start=${start}&end=${end}&timezone=${this.timezone}`
            );
            const data = this._processDisconnectionRepResponse(response.data);
            return data;
        } catch (error) {
            console.error(
                'Failed to fetch disconnection report due to: ',
                error
            );
            // Show error notification to user
            Notify.create({
                message: i18n.global.t(
                    'Failed to fetch disconnection data. Please try again later.'
                ),
                color: 'red',
                textColor: 'white',
                position: 'top'
            });
        }
    }

    /**
     * Process the data from the API to be used by the frontend.
     * @param {Object} response
     * @returns Formatted object
     */
    _processDisconnectionRepResponse(response) {
        const data = {
            start: response.start,
            end: response.end,
            report: []
        };

        response.disconnections.forEach(deviceDisconnection => {
            const processedDeviceDisconnectionReport =
                new DeviceDisconnectionReport(deviceDisconnection);
            data.report.push(processedDeviceDisconnectionReport);
        });

        return data;
    }

    // AUXILIARY FUNCTIONS
    /**
     * Get the period between two dates in seconds for uptime calculations
     * @param {String} start - Starting date for the period
     * @param {String} end - Ending date for the period
     * @returns {Number} - Period between two date in seconds
     */
    _getPeriodInSeconds(start, end) {
        const startDate = new Date(start);
        const endDate = new Date(end);
        return Math.abs(startDate.getTime() - endDate.getTime()) / 1000;
    }

    /**
     * Calculate the uptime based on the period and disconnection duration for the same period
     * @param {Number} periodInSeconds - Monitoring period in seconds
     * @param {Number} disconnectedDurationInSeconds - Total disconnected period in seconds
     * @returns {Float} - Uptime value
     */
    _getUptime(periodInSeconds, disconnectedDurationInSeconds) {
        if (periodInSeconds == 0 || disconnectedDurationInSeconds == 0) {
            return 100;
        }
        if (disconnectedDurationInSeconds > periodInSeconds) {
            return 0;
        }
        const downtime =
            (disconnectedDurationInSeconds / periodInSeconds) * 100;
        const uptime = 100 - downtime;
        return uptime;
    }

    /**
     * Compare the name of two devices to order them
     * @param {Object} a - Device A
     * @param {Object} b - Device B
     * @returns {Number}
     */
    _compareDevicesSerialNumber(a, b) {
        if (a.serialNumber < b.serialNumber) {
            return -1;
        }
        if (a.serialNumber > b.serialNumber) {
            return 1;
        }
        return 0;
    }

    /**
     * Creates the timeline array with one object per day
     * The API response has only the days with disconnections
     * The zero disconnections days need to be created and added to the timeline array
     * @param {String} start - Starting date for the period
     * @param {String} end - Ending date for the period
     * @param {Map} disconnectionsMap - Map with all disconnections with the formatted day as key
     * @returns {Array} - Timeline with all disconnections and no gaps
     */
    _fillDisconnectionGaps(start, end, disconnectionsMap) {
        const timelineWithoutGaps = [];
        // Set the starting date to fill the timeline
        let date = start;

        // Get the total period in days
        const periodInSeconds = this._getPeriodInSeconds(start, end);
        const periodInDays = periodInSeconds / (3600 * 24);

        // Fill the array until we have one object per day
        while (timelineWithoutGaps.length <= periodInDays) {
            const currentDate = getFormattedDate(date);
            if (disconnectionsMap.has(currentDate)) {
                timelineWithoutGaps.push(disconnectionsMap.get(currentDate));
            } else {
                timelineWithoutGaps.push({
                    durationInSeconds: 0,
                    count: 0,
                    hasDisconnection: false,
                    date: currentDate
                });
            }
            date = this._getNextDay(date);
        }
        return timelineWithoutGaps;
    }
    /**
     * Get the next day from a date
     * @param {String} date
     * @returns - The next day in a string
     */
    _getNextDay(date) {
        let nextDay = new Date(date);
        nextDay.setDate(nextDay.getDate() + 1);
        return nextDay;
    }

    /**
     * Creates the disconnection report object
     * @param {String} start - Starting date for the period
     * @param {String} end - Ending date for the period
     * @param {Object} report - Formatted data with timeline array
     * @returns {Object} - Formatted report with all data and timeline array
     */
    _buildDisconnectionReport(start, end, report) {
        // Fill timeline with no disconnection days
        const fullTimeline = this._fillDisconnectionGaps(
            start,
            end,
            report.timeline
        );

        // Get the period between dates in seconds
        const totalPeriodDurationInSeconds = this._getPeriodInSeconds(
            start,
            end
        );
        // Calculate uptime
        const uptime = this._getUptime(
            totalPeriodDurationInSeconds,
            report.totalDurationInSeconds
        );

        report.uptime = uptime;
        report.timeline = fullTimeline;

        return report;
    }

    // COMPONENT-FACING FUNCTIONS
    /**
     * Get all devices for a given facility with disconnection timeline
     * @param {Number} facility_id - Facility ID
     * @returns {Array} - Array of devices objects with disconnection timeline
     */
    async getAllDevices(facility_id) {
        // Get devices and disconnections report data from the API
        const devicesData = await this._fetchAllDevices(facility_id);
        // Order devices by serialName
        devicesData['results'].sort(this._compareDevicesSerialNumber);

        return devicesData['results'];
    }

    async getAllDisconnections(query_params) {
        return await this._fetchAllDisconnections(query_params);
    }

    /**
     * Get all devices for a given facility with disconnection timeline
     * @param {Number} facility
     * @param {String} start - Starting date for the period
     * @param {String} end - Ending date for the period
     * @returns {Object} - An object with start, end dates and a map of disconnections
     */
    async getDisconnectionsReport(facility, start, end) {
        if (!facility || !start || !end) {
            return;
        }

        // Check if start date is before the end date
        if (new Date(start) > new Date(end)) {
            return;
        }

        const data = await this._fetchDisconnectionsReport(
            facility,
            start,
            end
        );

        // turn report data into a map
        const disconnectionsMap = new Map();

        data.report.forEach(deviceReport => {
            const processedReport = this._buildDisconnectionReport(
                data.start,
                data.end,
                deviceReport
            );
            disconnectionsMap.set(deviceReport.id, processedReport);
        });

        return {
            start: data.start,
            end: data.end,
            report: disconnectionsMap
        };
    }
    /**
     * Generate report data for a device with no disconnections
     * This is used to fill the timeline with no disconnection days
     * @param {String} start - Starting date for report period in ISO
     * @param {String} end - Ending date for report period in ISO
     * @returns
     */
    generateDefaultReportData(start, end) {
        // Create a device report object with default values
        const deviceReport = new DeviceDisconnectionReport();
        // Fill timeline
        const fullTimeline = this._fillDisconnectionGaps(
            start,
            end,
            deviceReport.timeline
        );
        deviceReport.timeline = fullTimeline;
        return deviceReport;
    }
}
